import React, { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock, Share2 } from "lucide-react";
import { getBlog, blogs } from "../data/blogs";
import Nav from "../components/landing/Nav";
import Footer from "../components/landing/Footer";
import { BlogCover } from "./BlogsPage";

export default function BlogDetailPage({ onDemoClick }) {
  const { slug } = useParams();
  const blog = getBlog(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!blog) return <Navigate to="/blogs" replace />;

  const others = blogs.filter((b) => b.slug !== slug).slice(0, 3);

  return (
    <div
      className="relative min-h-screen bg-[#0a0a10] noise"
      data-testid="blog-detail-root"
    >
      <Nav onDemoClick={onDemoClick} />
      <main className="pt-32 md:pt-40 pb-20">
        <article className="max-w-3xl mx-auto px-6 md:px-12">
          <Link
            to="/blogs"
            data-testid="blog-back-link"
            className="inline-flex items-center gap-2 text-[12px] font-mono uppercase tracking-[0.14em] text-white/50 hover:text-white mb-10"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to journal
          </Link>

          <div className="flex items-center gap-2 mb-6">
            <span className="pill bg-[#7C5CFF]/15 border border-[#7C5CFF]/30 text-[#b9a9ff]">
              {blog.category}
            </span>
            <span className="text-[11px] font-mono text-white/45 inline-flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {blog.readTime} · {blog.date}
            </span>
          </div>

          <h1
            data-testid="blog-title"
            className="font-serif text-4xl md:text-6xl leading-[1.02] tracking-tight text-white"
          >
            {blog.title}
          </h1>
          <p className="mt-6 text-[18px] md:text-[20px] leading-relaxed text-white/60">
            {blog.teaser}
          </p>

          <div className="mt-8 pb-8 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img
                src={blog.hero}
                alt={blog.author}
                className="w-10 h-10 rounded-full object-cover grayscale"
              />
              <div>
                <div className="text-[14px] font-medium text-white">
                  {blog.author}
                </div>
                <div className="text-[12px] text-white/45">{blog.role}</div>
              </div>
            </div>
            <button
              data-testid="blog-share-btn"
              onClick={() => {
                if (navigator.share) {
                  navigator.share({ title: blog.title, url: window.location.href });
                } else if (navigator.clipboard) {
                  navigator.clipboard.writeText(window.location.href);
                }
              }}
              className="btn-ghost px-3 py-1.5 rounded-full text-[12px] text-white/80 inline-flex items-center gap-1.5"
            >
              <Share2 className="w-3.5 h-3.5" /> Share
            </button>
          </div>
        </article>

        {/* Visual section */}
        <div className="max-w-3xl mx-auto px-6 md:px-12 mt-10">
          <div
            className="rounded-2xl border border-white/10 bg-[#0b0b13] p-6"
            data-testid="blog-visual"
          >
            <div className="text-[11px] font-mono uppercase tracking-[0.14em] text-white/40 mb-4">
              ── Visual · {blog.visual}
            </div>
            <div className="h-[320px]">
              <BlogCover kind={blog.visual} title={blog.title} />
            </div>
          </div>
        </div>

        {/* Body */}
        <article className="max-w-3xl mx-auto px-6 md:px-12 mt-14 space-y-12">
          {blog.sections.map((s, i) => (
            <section key={i} data-testid={`blog-section-${i}`}>
              <h2 className="font-serif text-[28px] md:text-[32px] leading-[1.15] text-white mb-4">
                {s.h}
              </h2>
              <p className="text-[16.5px] md:text-[17.5px] leading-[1.8] text-white/75">
                {s.p}
              </p>
            </section>
          ))}
        </article>

        {/* CTA strip */}
        <div className="max-w-3xl mx-auto px-6 md:px-12 mt-20">
          <div className="grad-border p-8 md:p-10 text-center">
            <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-[#3DDFB3] mb-3">
              ── Try it yourself
            </div>
            <h3 className="font-serif text-3xl md:text-4xl text-white leading-tight mb-3">
              See these ideas{" "}
              <span className="italic">running live.</span>
            </h3>
            <p className="text-white/55 max-w-xl mx-auto mb-6 text-[15px]">
              30-minute walkthrough with a solutions engineer. No sales pitch
              — we just ship agents.
            </p>
            <button
              data-testid="blog-cta-btn"
              onClick={onDemoClick}
              className="btn-primary text-white px-6 py-2.5 rounded-full text-[14px] font-medium inline-flex items-center gap-2"
            >
              Request demo
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Related */}
        <div className="max-w-7xl mx-auto px-6 md:px-12 mt-24">
          <div className="flex items-end justify-between mb-8">
            <h3 className="font-serif text-3xl md:text-4xl text-white">
              Keep reading
            </h3>
            <Link
              to="/blogs"
              className="text-[13px] text-[#7C5CFF] inline-flex items-center gap-1.5 hover:gap-2.5 transition-all"
              data-testid="blog-all-link"
            >
              All essays <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {others.map((b, i) => (
              <Link
                to={`/blogs/${b.slug}`}
                key={b.slug}
                className="card-surface p-6 group"
                data-testid={`blog-related-${i}`}
              >
                <BlogCover kind={b.visual} compact />
                <div className="mt-4 text-[11px] font-mono uppercase tracking-[0.12em] text-white/45">
                  {b.category} · {b.readTime}
                </div>
                <h4 className="mt-2 font-serif text-[20px] leading-[1.2] text-white">
                  {b.title}
                </h4>
              </Link>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
